import React, { useMemo, useState } from 'react';
import { Download, Eye, RefreshCw, Trash2, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { DocumentAttachment } from '../../../services/legacyApi';

const ACCEPTED_DOCUMENT_TYPES = '.pdf,.dwg,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt';

type Props = {
    title: string;
    emptyLabel: string;
    documents: DocumentAttachment[];
    loading?: boolean;
    canUpload: boolean;
    canDownload: boolean;
    canDelete: boolean;
    onUpload: (payload: { file: File; note?: string; markCurrent: boolean }) => Promise<void>;
    onSetCurrent: (docId: number) => Promise<void>;
    onPreview?: (doc: DocumentAttachment) => void;
    onDownload: (docId: number) => Promise<void>;
    onDelete: (docId: number) => Promise<void>;
    onRefresh?: () => Promise<void>;
};

const formatBytes = (value: number) => {
    const bytes = Number(value || 0);
    if (bytes <= 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentAttachmentsCard: React.FC<Props> = ({
    title,
    emptyLabel,
    documents,
    loading = false,
    canUpload,
    canDownload,
    canDelete,
    onUpload,
    onSetCurrent,
    onPreview,
    onDownload,
    onDelete,
    onRefresh,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [noteText, setNoteText] = useState('');
    const [markCurrent, setMarkCurrent] = useState(true);
    const [busyDocId, setBusyDocId] = useState<number | null>(null);
    const [uploading, setUploading] = useState(false);
    const hasDocs = documents.length > 0;

    const sortedDocs = useMemo(
        () =>
            [...documents].sort((a, b) => {
                if (a.is_current && !b.is_current) return -1;
                if (!a.is_current && b.is_current) return 1;
                return Number(b.version_no || 0) - Number(a.version_no || 0);
            }),
        [documents]
    );

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            await onUpload({
                file: selectedFile,
                note: noteText.trim() || undefined,
                markCurrent,
            });
            setSelectedFile(null);
            setNoteText('');
            setMarkCurrent(true);
        } finally {
            setUploading(false);
        }
    };

    const runDocAction = async (docId: number, fn: () => Promise<void>) => {
        setBusyDocId(docId);
        try {
            await fn();
        } finally {
            setBusyDocId(null);
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
                <CardTitle>{title}</CardTitle>
                {onRefresh && (
                    <Button variant="outline" size="sm" onClick={() => onRefresh()}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {canUpload && (
                    <div className="rounded border p-3 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <input
                                type="file"
                                className="text-sm"
                                accept={ACCEPTED_DOCUMENT_TYPES}
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                            />
                            <input
                                type="text"
                                className="rounded border px-3 py-2 text-sm"
                                placeholder="Note (optional)"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                            />
                            <div className="flex items-center justify-between gap-3">
                                <label className="flex items-center gap-2 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={markCurrent}
                                        onChange={(e) => setMarkCurrent(e.target.checked)}
                                    />
                                    Mark as current
                                </label>
                                <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading documents...</div>
                ) : !hasDocs ? (
                    <div className="text-sm text-muted-foreground">{emptyLabel}</div>
                ) : (
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b">
                                <tr>
                                    <th className="px-3 py-2 text-left">Version</th>
                                    <th className="px-3 py-2 text-left">File</th>
                                    <th className="px-3 py-2 text-left">Note</th>
                                    <th className="px-3 py-2 text-left">Uploaded</th>
                                    <th className="px-3 py-2 text-right">Size</th>
                                    <th className="px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {sortedDocs.map((doc) => {
                                    const isBusy = busyDocId === doc.doc_id;
                                    return (
                                        <tr key={doc.doc_id}>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    <span>v{doc.version_no}</span>
                                                    {doc.is_current && (
                                                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                                                            Current
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 font-medium">{doc.original_file_name || '-'}</td>
                                            <td className="px-3 py-2 text-slate-600">{doc.note_text || '-'}</td>
                                            <td className="px-3 py-2 text-slate-600">
                                                <div>{doc.uploaded_by || '-'}</div>
                                                <div className="text-xs">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : '-'}</div>
                                            </td>
                                            <td className="px-3 py-2 text-right">{formatBytes(doc.size_bytes)}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex justify-end items-center gap-2">
                                                    {!doc.is_current && canUpload && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            disabled={isBusy}
                                                            onClick={() => runDocAction(doc.doc_id, () => onSetCurrent(doc.doc_id))}
                                                        >
                                                            Set Current
                                                        </Button>
                                                    )}
                                                    {doc.is_previewable && onPreview && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            aria-label={`Preview ${doc.original_file_name || 'document'}`}
                                                            disabled={isBusy}
                                                            onClick={() => onPreview(doc)}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {canDownload && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            aria-label={`Download ${doc.original_file_name || 'document'}`}
                                                            disabled={isBusy}
                                                            onClick={() => runDocAction(doc.doc_id, () => onDownload(doc.doc_id))}
                                                        >
                                                            <Download className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {canDelete && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-red-600"
                                                            disabled={isBusy}
                                                            onClick={() => runDocAction(doc.doc_id, () => onDelete(doc.doc_id))}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
